import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { HiTrash } from 'react-icons/hi';
import { toast } from 'react-toastify';
import { isEmpty } from 'lodash';
import useSelectedBusiness from '../../../hooks/business/useSelectedBusiness';
import { businessTypeToString } from '../../../utils/stringUtils';
import QuestionCircle from '../../components/misc/QuestionCircle';
import DeleteBusinessModal from '../../components/modals/DeleteBusinessModal';
import UpgradeModal from '../../components/modals/UpgradeModal';
import DisableMemberAdministrationModal from '../../components/modals/DisableMemberAdministrationModal';
import businessService from '../../../services/businessService';
import inviteService from '../../../services/inviteService';
import { AdminRole, MemberPreferences } from '../../../types/business';
import { isCommunity } from '../../../constants';
import useBusinessTranslation from '../../../hooks/useBusinessTranslation';
import Button from '../../components/buttons/Button';
import ImageInput from '../../components/forms/ImageInput';
import useBusinessEffect from '../../../hooks/effects/useBusinessEffect';
import PageHeader from '../../components/misc/PageHeader';
import FeaturesForm from '../../components/misc/FeaturesForm';
import MemberPreferenceForm, {
  getDefaultMemberPreferences,
} from '../../components/misc/MemberPreferenceForm';
import StorageUsage from '../../components/misc/StorageUsage';
import { Features, getMinimumSubscriptionForFeatures } from '../../../utils/FeatureUtils';
import { SubscriptionType } from '../../../types/misc';
import { BusinessInvitePropertyType, BusinessInvite } from '../../../services/model/inviteService.model';

export default function GeneralSettingsPage(): JSX.Element {
  const business = useSelectedBusiness();
  const location = useLocation();
  const { t } = useBusinessTranslation();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = React.useState<'general' | 'features' | 'memberPreferences'>(
    location.state?.view ? location.state.view : 'general'
  );
  const [deleteDialog, setDeleteDialog] = React.useState<boolean>(false);
  const [upgradeModal, setUpgradeModal] = React.useState<{
    open: boolean;
    requiredSubscription: SubscriptionType;
    featureName: string;
  }>({
    open: false,
    requiredSubscription: SubscriptionType.FREE,
    featureName: '',
  });
  const [showDisableModal, setShowDisableModal] = React.useState(false);
  const [currentInvite, setCurrentInvite] = React.useState<BusinessInvite | undefined>();
  const [name, setName] = React.useState<string>(business?.name!);
  const [features, setFeatures] = React.useState<Features>(business?.features || {});
  const [memberPreferences, setMemberPreferences] = React.useState<MemberPreferences>(
    isEmpty(business?.memberPreferences)
      ? getDefaultMemberPreferences()
      : business!.memberPreferences,
  );
  const [description, setDescription] = React.useState<string>(business?.description!);
  const [profileImageUrl, setProfileImageUrl] = React.useState<string | undefined>(
    business?.picture!,
  );
  const [profileImageAction, setProfileImageAction] = React.useState<'UPLOAD' | 'DELETE'>();

  useBusinessEffect(() => {
    setName(business?.name!);
    setDescription(business?.description!);
    setProfileImageUrl(business?.picture!);
  }, []);

  const getUpdateRequiredFeature = (): string => {
    const newFeatures = Object.keys(features).filter(
      (key) => features[key as keyof Features] && !business?.features?.[key as keyof Features],
    );

    return newFeatures.length > 0 ? 
      newFeatures[0].split('_')
      .map((word, index) => 
        index === 0 
          ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase() 
          : word.toLowerCase()
      )
      .join(' ') : '';
  };

  const handleSave = async (): Promise<void> => {
    try {
      if (business?.features?.MEMBER_ADMINISTRATION && !features.MEMBER_ADMINISTRATION) {
        try {
          const fields = await inviteService.fetchInvite();
          if (fields && fields.nonRequestedFields.length !== 10) {
            setCurrentInvite(fields);
            setShowDisableModal(true);
            return;
          }
        } catch {
          toast.error(t('toast.error.memberAdministrationFeature'));
          return;
        }
      }

      await performSave();
    } catch {
      toast.error(t('toast.error.imageWrong'));
      setProfileImageAction(undefined);
    }
  };

  const performSave = async (): Promise<void> => {
    try {
      if (features !== business?.features) {
        let currentSubscription;
        if (!business) {
          currentSubscription = SubscriptionType.FREE;
        } else if (business.trialEndDate && new Date(business.trialEndDate) > new Date()) {
          currentSubscription = business.trialType || SubscriptionType.FREE;
        } else {
          currentSubscription = business.subscription?.subscriptionType ?? SubscriptionType.FREE;
        }
        const requiredSubscription = getMinimumSubscriptionForFeatures(features);

        if (requiredSubscription === SubscriptionType.BASE && currentSubscription === SubscriptionType.FREE) {
          setUpgradeModal({
            open: true,
            requiredSubscription: SubscriptionType.BASE,
            featureName: getUpdateRequiredFeature(),
          });
          return;
        }
        
        if (requiredSubscription === SubscriptionType.PRO && currentSubscription !== SubscriptionType.PRO) {
          setUpgradeModal({
            open: true,
            requiredSubscription: SubscriptionType.PRO,
            featureName: getUpdateRequiredFeature(),
          });
          return;
        }
      }

      await businessService.updateBusiness(name, description, features, memberPreferences);

      if (features.MEMBER_ADMINISTRATION && !business?.features?.MEMBER_ADMINISTRATION) {
        try {
          const inviteData = await inviteService.fetchInvite();
          
          const emailAlreadyRequested = 
            inviteData.mandatoryFields.includes(BusinessInvitePropertyType.EMAIL) ||
            inviteData.optionalFields.includes(BusinessInvitePropertyType.EMAIL);
          
          if (!emailAlreadyRequested) {
            const updatedInvite = {
              ...inviteData,
              optionalFields: [...inviteData.optionalFields, BusinessInvitePropertyType.EMAIL],
              nonRequestedFields: inviteData.nonRequestedFields.filter(field => field !== BusinessInvitePropertyType.EMAIL)
            };
            
            await inviteService.updateInvite(updatedInvite);
          }
        } catch (error) {
          console.error('Failed to update invite with EMAIL field:', error);
        }
      }

      if (profileImageAction === 'UPLOAD' && profileImageUrl) {
        await businessService.setProfileImage(
          await fetch(profileImageUrl!).then((res) => res.blob() as Promise<File>),
        );
      } else if (profileImageAction === 'DELETE') {
        await businessService.deleteProfileImage();
      }

      toast.success(t('toast.success.businessSaved'));
      setProfileImageAction(undefined);
    } catch {
      toast.error(t('toast.error.imageWrong'));
      setProfileImageAction(undefined);
    }
  };

  const handleDisableMemberAdministration = async () => {
    if (currentInvite) {
      try {
        const allFields = [...currentInvite.mandatoryFields, ...currentInvite.optionalFields];
        const updatedInvite = {
          ...currentInvite,
          mandatoryFields: [],
          optionalFields: [],
          nonRequestedFields: [...currentInvite.nonRequestedFields, ...allFields],
          customFields: [],
        };
        
        await inviteService.updateInvite(updatedInvite);
      } catch (error) {
        console.error('Failed to clear invite data:', error);
        toast.error(t('toast.error.failedToClearData'));
        return;
      }
    }
    
    await performSave();
  };

  const handleUpgrade = () => {
    navigate('/settings/subscriptions');
  };

  const isUnchanged =
    description === business?.description &&
    name === business?.name &&
    !profileImageAction &&
    features === business?.features &&
    memberPreferences === business?.memberPreferences;

  return (
    <>
      <div>
        <PageHeader
          title={t('page.settings.general.title')}
          subtitle={t('page.settings.subtitle', { type: businessTypeToString(business?.type!) })}
        />

        {/* Tabs */}
        <div className="flex gap-4 border-b border-primary-300">
          {(['general', 'features', 'memberPreferences'] as const).map((tabKey) => (
            <button
              key={tabKey}
              type="button"
              onClick={() => setActiveTab(tabKey)}
              className={`relative px-4 pb-1 pt-2 font-medium transition-all duration-150 ${
                activeTab === tabKey
                  ? 'text-primary-700 -mb-[1px] rounded-t-md border border-primary-300 border-b-secondary-50'
                  : 'text-primary-600 hover:text-primary-800'
              }`}>
              {t(`page.settings.general.tabs.${tabKey}`)}
            </button>
          ))}
        </div>

        <div className="mb-8 flex flex-col gap-6 lg:flex-row">
          {/* Left: Only for General tab */}
          {activeTab === 'general' && (
            <div className="w-full pt-8 lg:w-1/2 lg:py-8">
              <div className="flex items-center">
                <h2 className="mb-2 mr-1 mt-1 text-xl font-medium">
                  {t('page.settings.general.image.title')}
                </h2>
                <QuestionCircle className="-left-10 top-7 w-[300px]">
                  {t('page.settings.general.image.explanation')}
                </QuestionCircle>
              </div>
              <ImageInput
                className="h-64 lg:h-72"
                iconClassName="w-14 h-14"
                image={profileImageUrl}
                setImage={setProfileImageUrl}
                setImageAction={setProfileImageAction}
              />
            </div>
          )}

          {/* Right section */}
          <div className="w-full py-8 lg:flex-1">
            {activeTab === 'general' && (
              <>
                <h2 className="mb-2 mt-1 text-xl font-medium">
                  {t('page.settings.general.generalData.title')}
                </h2>

                <div className="flex flex-col py-4">
                  <label className="-mt-4 pb-1 pl-1 pr-2 font-medium">{t('general.name')}</label>
                  <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    type="text"
                    placeholder={t('page.settings.general.generalData.namePlaceholder')}
                  />
                </div>

                {isCommunity && (
                  <>
                    <div className="flex flex-col py-4">
                      <label className="-mt-4 pb-1 pl-1 pr-2 font-medium">
                        {t('general.description')}
                      </label>
                      <textarea
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        className="h-28"
                        placeholder={t('page.settings.general.generalData.descriptionPlaceholder')}
                      />
                    </div>
                  </>
                )}
              </>
            )}

            {activeTab === 'features' && isCommunity && (
              <div className="w-2/3">
                {/* Storage Usage */}
                <h2 className="mb-2 mt-1 text-xl font-medium">
                  {t('page.settings.general.tabs.storage')}
                </h2>
                <div className="mb-4">
                  <StorageUsage />
                </div>

                {/* Features */}
                <h2 className="mb-2 mt-1 text-xl font-medium">
                  {t('page.settings.general.tabs.features')}
                </h2>
                <FeaturesForm features={features} onFeaturesChange={setFeatures} />
              </div>
            )}

            {activeTab === 'memberPreferences' && isCommunity && (
              <div className="w-full pt-8 lg:w-1/2 lg:py-8">
                <h2 className="mb-2 mt-1 text-xl font-medium">
                  {t('page.settings.general.tabs.memberPreferences')}
                </h2>
                <MemberPreferenceForm
                  disableEventPreference={!features.EVENTS}
                  disableScoreCardPreference={!features.STATS_AND_SCORES}
                  disableTaskPreference={!features.TASKS}
                  disablePhotoAlbumPreference={!features.PHOTO_ALBUMS}
                  initialMemberPreferences={memberPreferences}
                  onMemberPreferencesChange={setMemberPreferences}
                />
              </div>
            )}

            <div className="mt-10 flex justify-end">
              <Button
                variant="primary"
                onClick={handleSave}
                className="px-8"
                disabled={isUnchanged || !name}>
                {t('general.save')}
              </Button>
            </div>
          </div>
        </div>

        {/* Delete Business */}
        {business?.role === AdminRole.OWNER && (
          <div className="flex w-full justify-end">
            <Button onClick={() => setDeleteDialog(true)} className="underline">
              {t('page.settings.general.delete')}
              <HiTrash className="ml-1 h-5 w-5" />
            </Button>
          </div>
        )}
      </div>

      <DeleteBusinessModal open={deleteDialog} setOpen={setDeleteDialog} />
      <UpgradeModal
        open={upgradeModal.open}
        setOpen={(open) => setUpgradeModal(prev => ({ ...prev, open }))}
        requiredSubscription={upgradeModal.requiredSubscription}
        featureName={upgradeModal.featureName}
        onUpgrade={handleUpgrade}
      />
      <DisableMemberAdministrationModal
        open={showDisableModal}
        setOpen={setShowDisableModal}
        currentInvite={currentInvite}
        onConfirmDisable={handleDisableMemberAdministration}
      />
    </>
  );
}
